import { useEffect, useState, useCallback } from "react";
import { useParams, Link as RouterLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Box,
  Breadcrumbs,
  Button,
  CardMedia,
  Typography,
  Link,
  Paper,
  Divider,
  Collapse,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  SentimentDissatisfied as NoDataIcon,
} from "@mui/icons-material";

import { useUtility } from "utils/context/UtilityContext";
import {
  CountryInfo,
  GetProductsQuery,
  getYoutubeEmbedUrl,
  normalizeCountries,
  Product,
  SetFlag,
  useWindowSize,
} from "utils";
import {
  getProducts,
  getProviderByName,
} from "utils/api/private/ProductApiService";
import { Header } from "../Header";
import { Loading } from "../Loading";
import { Footer } from "../footer";
import { RssFeed } from "../RssFeed";
import { ProductList } from "./ProductList";
import { Provider, QAType } from "utils/types/Provider.types";

const getCountryKey = (provider?: string) =>
  `selectedCountry_${provider ?? ""}`;
const safeGet = (k: string) =>
  typeof window === "undefined" ? null : localStorage.getItem(k);
const safeSet = (k: string, v: string) => {
  if (typeof window !== "undefined") localStorage.setItem(k, v);
};

export const ProviderProductsPage = () => {
  const { provider } = useParams<{ provider: string }>();
  const navigate = useNavigate();

  // State for provider-specific data (description, logo, etc.)
  const [providerData, setProviderData] = useState<Provider | null>(null);
  const [countries, setCountries] = useState<CountryInfo[]>([]);

  // State for the product list and its server-side query
  const [products, setProducts] = useState<Product[]>([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [query, setQuery] = useState<GetProductsQuery>({
    provider: provider || "",
    page: 1,
    limit: 12,
    sortBy: "priceUSD",
    order: "asc",
  });

  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [expandedQA, setExpandedQA] = useState<string[]>([]);
  const { marginBottomHeader } = useUtility();
  const { isMobile } = useWindowSize();

  // Fetch static provider details once
  useEffect(() => {
    const fetchProviderDetails = async () => {
      if (!provider) return;
      try {
        const { provider: data, countries: countryList } =
          await getProviderByName(provider);
        setProviderData(data);
        const normalized = normalizeCountries(countryList);
        setCountries(normalized);

        // init country en string
        const saved = safeGet(getCountryKey(provider));
        const existsInList = saved && normalized.some((c) => c.code === saved);
        const initialCode = (existsInList ? saved : normalized[0]?.code) || "";

        if (!query.country && initialCode) {
          setQuery((prev) => ({ ...prev, country: initialCode }));
          // Persist the initial pick so next visit uses it
          safeSet(getCountryKey(provider), initialCode);
        }
      } catch (err) {
        console.error("Failed to fetch provider details:", err);
        setError("Impossible de charger les informations du fournisseur.");
      }
    };
    fetchProviderDetails();
  }, [provider, query.country]);

  useEffect(() => {
    const v: any = query.country;
    if (v && typeof v === "object" && "code" in v) {
      setQuery((prev) => ({ ...prev, country: String(v.code) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.country]);
  // Fetch products whenever the query (filters, sort, page) changes
  const fetchProviderProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getProducts(query);
      setProducts(response.data);
      setTotalProducts(response.meta.total);
    } catch (err: any) {
      console.error("Failed to fetch products:", err);
      setError(err.message || "Impossible de charger les produits.");
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    // Only fetch products if a provider has been loaded
    if (query.provider) {
      fetchProviderProducts();
    }
  }, [fetchProviderProducts, query.provider]);

  const handleExpandClick = (index: string) => {
    setExpandedQA((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  if (!provider) {
    return <div>Provider not specified.</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`${marginBottomHeader} flex flex-col items-center justify-start gap-4 overflow-x-hidden fancy-scroll`}
    >
      <Header />
      <Box className="flex flex-col items-center justify-start gap-4 w-full">
        {/* --- HERO SECTION --- */}
        <div
          className="relative w-full h-72 flex flex-col items-center justify-center text-center bg-gradient-to-r from-gray to-variant_1"
          style={{ clipPath: "polygon(0 0, 100% 0, 100% 85%, 0 100%)" }}
        >
          <Breadcrumbs
            aria-label="breadcrumb"
            sx={{ mb: 2, p: "1%", width: "100%" }}
          >
            <Link
              component={RouterLink}
              underline="hover"
              color="inherit"
              to="/"
            >
              Accueil
            </Link>
            <Link
              component={RouterLink}
              underline="hover"
              color="inherit"
              to="/products"
            >
              Produits
            </Link>
            <Typography color="text.primary">{provider}</Typography>
          </Breadcrumbs>
          {providerData?.url && (
            <CardMedia
              component="img"
              image={providerData.url}
              alt={`${provider} logo`}
              sx={{ height: isMobile ? 100 : 140, objectFit: "contain", mb: 2 }}
            />
          )}
          <Typography variant="h4" gutterBottom>
            Carte Cadeau {provider}
          </Typography>
        </div>

        <Box sx={{ width: { xs: "95%", md: "85%" }, p: 2 }}>
          {/* --- FILTER & SORT CONTROLS --- */}
          <Paper
            sx={{ p: 2, mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }}
          >
            <FormControl sx={{ minWidth: 180, flexGrow: 1 }}>
              <InputLabel>Pays</InputLabel>
              <Select<string>
                value={query.country || ""}
                label="Pays"
                onChange={(e) => {
                  const next = String(e.target.value);
                  setQuery((prev) => ({ ...prev, country: next, page: 1 }));
                  safeSet(getCountryKey(provider), next); // ← persist per provider
                }}
                renderValue={(v) => {
                  if (!v)
                    return (
                      <span style={{ opacity: 0.6 }}>Sélectionner un pays</span>
                    );
                  const found = countries.find((c) => c.code === v);
                  if (!found) return v; // fallback string
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <SetFlag
                        countryName={found.name}
                        countryCode={found.code}
                      />
                      <span>{found.name}</span>
                    </Box>
                  );
                }}
              >
                {countries.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <SetFlag countryName={c.name} countryCode={c.code} />
                      <span>{c.name}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 180, flexGrow: 1 }}>
              <InputLabel>Trier par</InputLabel>
              <Select
                value={`${query.sortBy}-${query.order}`}
                label="Trier par"
                onChange={(e) => {
                  const [sortBy, order] = e.target.value.split("-");
                  setQuery((prev) => ({
                    ...prev,
                    sortBy,
                    order: order as "asc" | "desc",
                  }));
                }}
              >
                <MenuItem value="priceUSD-asc">Prix (croissant)</MenuItem>
                <MenuItem value="priceUSD-desc">Prix (décroissant)</MenuItem>
                <MenuItem value="name-asc">Nom (A-Z)</MenuItem>
                <MenuItem value="name-desc">Nom (Z-A)</MenuItem>
              </Select>
            </FormControl>
          </Paper>

          {/* --- PRODUCT LIST & PAGINATION --- */}
          {loading ? (
            <Loading />
          ) : products.length === 0 ? (
            <Box className="flex flex-col items-center justify-center gap-4 mt-8">
              <NoDataIcon style={{ fontSize: 50, color: "gray" }} />
              <Typography variant="h6" className="text-gray-700">
                Aucun produit trouvé pour cette sélection 😔
              </Typography>
              <Button variant="contained" onClick={() => navigate(-1)}>
                Retour
              </Button>
            </Box>
          ) : (
            <>
              <ProductList products={products} />
              <TablePagination
                component="div"
                count={totalProducts}
                page={query.page - 1}
                onPageChange={(e, newPage) =>
                  setQuery((prev) => ({ ...prev, page: newPage + 1 }))
                }
                rowsPerPage={query.limit}
                onRowsPerPageChange={(e) =>
                  setQuery((prev) => ({
                    ...prev,
                    limit: parseInt(e.target.value, 10),
                    page: 1,
                  }))
                }
                rowsPerPageOptions={[12, 24, 48]}
              />
            </>
          )}

          {/* --- PROVIDER INFO SECTION --- */}
          <Paper elevation={3} sx={{ p: 3, mt: 4, mx: { xs: 0, md: "10%" } }}>
            <Typography variant="h5" textAlign="center" gutterBottom>
              Découvrez plus avec {provider}
            </Typography>
            {providerData?.externalUrl && (
              <Button
                variant="contained"
                fullWidth
                href={providerData.externalUrl}
                target="_blank"
              >
                Utiliser votre carte cadeau sur le site de {provider}
              </Button>
            )}
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="body1"
              textAlign="justify"
              dangerouslySetInnerHTML={{
                __html: providerData?.description || "",
              }}
            />
            {providerData?.videoUrl && (
              <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
                <iframe
                  src={getYoutubeEmbedUrl(providerData.videoUrl)}
                  title="Video Content"
                  style={{
                    width: "100%",
                    maxWidth: "560px",
                    height: "315px",
                    borderRadius: "8px",
                    border: "none",
                  }}
                  allowFullScreen
                />
              </Box>
            )}
          </Paper>

          {/* --- FAQ SECTION --- */}
          {providerData?.qas && providerData.qas.length > 0 && (
            <Box mt={5} mx={{ xs: 0, md: "10%" }}>
              <Typography variant="h5" gutterBottom>
                Questions fréquemment posées
              </Typography>
              {providerData.qas.map((qa: QAType, index) => (
                <Paper key={qa.id} sx={{ mb: 1 }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    onClick={() => handleExpandClick(qa.id)}
                    sx={{ cursor: "pointer", p: 2 }}
                  >
                    <Typography
                      variant="body1"
                      dangerouslySetInnerHTML={{ __html: qa.question }}
                    />
                    <IconButton>
                      {expandedQA.includes(qa.id) ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )}
                    </IconButton>
                  </Box>
                  <Collapse
                    in={expandedQA.includes(qa.id)}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Typography
                      sx={{ p: 2, pt: 0 }}
                      dangerouslySetInnerHTML={{ __html: qa.answer }}
                    />
                  </Collapse>
                </Paper>
              ))}
            </Box>
          )}
          <div className="w-full px-4 md:px-8 lg:px16 xl:px-16">
                    {(provider && provider !=='BSB') && <RssFeed search={provider} />}     {" "}
          </div>
        </Box>
      </Box>
      <Footer />
    </motion.div>
  );
};
